import { Box, BoxProps } from "@mui/material";
import { ReactNode, useState } from "react";

import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import ViewContentBlockHeader from "@/App/Layouts/ViewContentBlockHeader";
import { EntityHelper } from "@/common/helpers/entity";
import { BaseEntityDtoAny, EntityAffiliationInfo } from "@/common/ts/entity";
import { AppPermission, EntityType } from "@/core/api/generated/v1";

import AuthorizedElement from "../../Auth/AuthorizedElement";
import AuthorizedTenantElement from "../../Auth/AuthorizedTenantElement";
import AppIconButton from "../../Button/AppIconButton";
import AppIcon from "../../Icons/AppIcon";
import EntityAffiliationInfoDisplay, {
  EntityAffiliationInfoDisplayProps,
} from "./EntityAffiliationInfoDisplay";
import EntityAffiliationUpdateCascadeModal, {
  EntityAffiliationUpdateCascadeModalProps,
} from "./EntityAffiliationUpdateCascadeModal";

interface Props {
  entityType: EntityType;
  entityId: Nil<string>;
  entity: Nil<BaseEntityDtoAny>;
  affiliationInfo?: Nil<EntityAffiliationInfo>;
  /** Permission required to show edit button. */
  entityUpdatePermission: AppPermission | undefined;
  title?: ReactNode;
  /** Show block wrapper (ViewContentBlock). @default true. */
  withContentBlockWrapper?: boolean;
  /** hide tenant row in display */
  infoDisplayProps?: Partial<EntityAffiliationInfoDisplayProps>;
  infoUpdateProps?: Partial<EntityAffiliationUpdateCascadeModalProps>;
  sx?: BoxProps["sx"];
  onUpdate: () => void;
}

export default function EntityAffiliationInfoBlock({
  entityType,
  entityId,
  entity,
  affiliationInfo,
  entityUpdatePermission,
  title,
  withContentBlockWrapper = true,
  infoDisplayProps,
  infoUpdateProps,
  sx,
  onUpdate,
}: Props) {
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);

  const affiliationInfoComputed = affiliationInfo || EntityHelper.getAffiliationInfo(entity);

  const header = (
    <ViewContentBlockHeader>
      {title || "Affiliation"}{" "}
      {entityUpdatePermission && (
        <AuthorizedTenantElement tenantIdsAny={[affiliationInfoComputed?.tenantId]}>
          <AuthorizedElement permissions={[entityUpdatePermission]}>
            <AppIconButton
              tooltipProps={{ title: "Edit entity affiliation", isDefaultEnterDelays: true }}
              onClick={() => setIsUpdateOpen(true)}
            >
              <AppIcon of='edit' fontSize='small' />
            </AppIconButton>
          </AuthorizedElement>
        </AuthorizedTenantElement>
      )}
    </ViewContentBlockHeader>
  );

  const content = (
    <>
      <EntityAffiliationInfoDisplay
        affiliationInfo={affiliationInfoComputed}
        tenantId={undefined}
        {...infoDisplayProps}
      />

      <EntityAffiliationUpdateCascadeModal
        open={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
        entityType={entityType}
        entityId={entityId}
        affiliationInfo={affiliationInfoComputed}
        onUpdate={onUpdate}
        {...infoUpdateProps}
      />
    </>
  );

  const renderWrapper = (children: ReactNode) => {
    return withContentBlockWrapper ? (
      <ViewContentBlock sx={sx}>{children}</ViewContentBlock>
    ) : (
      <Box sx={sx}>{children}</Box>
    );
  };

  return renderWrapper(
    <>
      {header}
      {content}
    </>,
  );
}
